import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Layout, { DateInput, TablePagination, usePagination } from "./Layout";
import { formatDate, getApiUrl } from "./config";

function VehicleTripReport() {
  const role = localStorage.getItem("role") || "user";
  const isAdmin = role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);

  const fetchVehicles = useCallback(async () => {
    const res = await axios.get(`${getApiUrl()}/vehicles`);
    setVehicles(res.data || []);
  }, []);

  const fetchTrips = useCallback(async () => {
    const params = {};
    if (selectedVehicleId) params.vehicle_id = selectedVehicleId;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    const res = await axios.get(`${getApiUrl()}/reports/vehicle-trips`, {
      params,
    });
    setRows(res.data || []);
  }, [selectedVehicleId, fromDate, toDate]);

  const reopenTrip = async (tripId) => {
    if (!tripId) return;
    if (!window.confirm("Reopen this trip?")) return;
    try {
      await axios.post(`${getApiUrl()}/vehicle-trips/${tripId}/reopen`);
      fetchTrips().catch(console.error);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.sqlMessage ||
        err?.response?.data?.code ||
        "Failed to reopen trip";
      alert(message);
    }
  };

  const handleExportPdf = () => {
    window.print();
  };

  useEffect(() => {
    fetchVehicles().catch(console.error);
  }, [fetchVehicles]);

  useEffect(() => {
    fetchTrips().catch(console.error);
  }, [fetchTrips]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    pagedItems,
    totalItems,
    startIndex,
    endIndex
  } = usePagination(rows, 10);

  const totals = rows.reduce(
    (acc, r) => {
      acc.loaded_qty += Number(r.loaded_qty) || 0;
      acc.loaded_weight += Number(r.loaded_weight) || 0;
      acc.unloaded_qty += Number(r.unloaded_qty) || 0;
      acc.unloaded_weight += Number(r.unloaded_weight) || 0;
      acc.dead_qty += Number(r.dead_qty) || 0;
      acc.weight_difference += Number(r.weight_difference) || 0;
      return acc;
    },
    {
      loaded_qty: 0,
      loaded_weight: 0,
      unloaded_qty: 0,
      unloaded_weight: 0,
      dead_qty: 0,
      weight_difference: 0,
    }
  );

  return (
    <Layout>
      <div className="container mt-4">
        <h2>Vehicle Trip Report</h2>

        <div className="row g-3 align-items-end mb-3 no-print">
          <div className="col-md-4">
            <label className="form-label fw-bold">Vehicle</label>
            <select className="form-select" value={selectedVehicleId} onChange={(e) => setSelectedVehicleId(e.target.value)}>
              <option value="">All Vehicles</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.vehicle_no}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold">From</label>
            <DateInput value={fromDate} onChange={setFromDate} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold">To</label>
            <DateInput value={toDate} onChange={setToDate} />
          </div>
          <div className="col-md-2 d-grid">
            <button type="button" className="btn btn-primary" onClick={() => fetchTrips().catch(console.error)}>
              Refresh
            </button>
          </div>
          <div className="col-md-2 d-grid">
            <button type="button" className="btn btn-outline-dark" onClick={handleExportPdf}>
              Export PDF
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Trip ID</th>
                <th>Vehicle</th>
                <th>Start Date</th>
                <th>Start KM</th>
                <th>End Date</th>
                <th>End KM</th>
                <th>Loaded Qty</th>
                <th>Loaded Weight</th>
                <th>Unloaded Qty</th>
                <th>Unloaded Weight</th>
                <th>Dead Qty</th>
                <th>Weight Diff</th>
                <th>Status</th>
                {isAdmin && <th className="no-print">Action</th>}
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{r.vehicle_no}</td>
                  <td>{formatDate(r.start_date)}</td>
                  <td>{r.start_km}</td>
                  <td>{formatDate(r.end_date)}</td>
                  <td>{r.end_km ?? "-"}</td>
                  <td>{r.loaded_qty}</td>
                  <td>{parseFloat(r.loaded_weight || 0).toFixed(2)}</td>
                  <td>{r.unloaded_qty ?? 0}</td>
                  <td>{parseFloat(r.unloaded_weight || 0).toFixed(2)}</td>
                  <td>{r.dead_qty ?? 0}</td>
                  <td>{parseFloat(r.weight_difference || 0).toFixed(2)}</td>
                  <td>{r.status}</td>
                  {isAdmin && (
                    <td className="no-print">
                      {String(r.status || "").toLowerCase() === "closed" ? (
                        <button type="button" className="btn btn-sm btn-warning" onClick={() => reopenTrip(r.id)}>
                          Reopen
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 14 : 13} className="text-center">
                    No trips found
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan="6" className="text-end">
                  Total
                </th>
                <th>{totals.loaded_qty}</th>
                <th>{totals.loaded_weight.toFixed(2)}</th>
                <th>{totals.unloaded_qty}</th>
                <th>{totals.unloaded_weight.toFixed(2)}</th>
                <th>{totals.dead_qty}</th>
                <th>{totals.weight_difference.toFixed(2)}</th>
                <th />
                {isAdmin && <th />}
              </tr>
            </tfoot>
          </table>
        </div>
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={setCurrentPage}
        />
      </div>
    </Layout>
  );
}

export default VehicleTripReport;
